import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/utils/api';
import { toast } from 'sonner';
import { 
  TrendingUp, 
  FileText, 
  Clock, 
  CheckCircle, 
  XCircle,
  DollarSign,
  AlertCircle,
  ArrowRight,
  Calendar,
  Users,
  Eye,
  BarChart3,
  Calculator,
  Briefcase,
  Activity,
  ChevronRight,
  MessageSquare
} from 'lucide-react';
import { KPIDrillDown, commonColumns } from '@/components/analytics/DrillDownHelpers';
import NotificationBell from '@/components/NotificationBell';

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalProposals: 0,
    pendingApprovals: 0,
    approvedThisMonth: 0,
    rejectedThisMonth: 0,
    totalRevenue: 0,
    recentSubmissions: [],
    draftCount: 0,
    avgTurnaround: null
  });
  const [pendingProposals, setPendingProposals] = useState([]);
  const [pendingScenarios, setPendingScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quickActionId, setQuickActionId] = useState(null);
  const [rejectComment, setRejectComment] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(null);
  
  // Drill-down data
  const [allProposalsData, setAllProposalsData] = useState([]);
  const [approvedThisMonthData, setApprovedThisMonthData] = useState([]);
  const [approvedRevenueData, setApprovedRevenueData] = useState([]);
  
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userRole = user.role || 'preparer';
  const isApprover = ['admin', 'approver', 'cfo', 'ceo'].includes(userRole);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      
      const requests = [api.get('/collections')];
      if (isApprover) {
        requests.push(api.get('/scenarios/pending-approval/list'));
      }

      const results = await Promise.all(requests);
      const proposalsResponse = results[0];
      const allProposals = proposalsResponse.data.collections || [];
      
      // Normalize proposals: add collection_name alias and compute days_pending for KPI drill-downs
      const normalizedProposals = allProposals.map(p => ({
        ...p,
        collection_name: p.collection_name || p.name,  // Ensure KPI table column 'collection_name' has data
        days_pending: p.submitted_at
          ? Math.round((Date.now() - new Date(p.submitted_at)) / (1000 * 60 * 60 * 24))
          : null
      }));
      
      const pending = normalizedProposals.filter(p => 
        p.status === 'pending_approval' || p.status === 'pending_cfo_approval' || p.status === 'pending_ceo_approval'
      );
      
      const drafts = normalizedProposals.filter(p => p.status === 'draft');
      
      const thisMonth = new Date();
      thisMonth.setDate(1);
      thisMonth.setHours(0, 0, 0, 0);
      
      const approvedThisMonth = normalizedProposals.filter(p => 
        p.status === 'approved' && new Date(p.updated_at) >= thisMonth
      );
      
      const rejectedThisMonth = normalizedProposals.filter(p =>
        p.status === 'rejected' && new Date(p.updated_at) >= thisMonth
      );
      
      const totalRevenue = normalizedProposals
        .filter(p => p.status === 'approved')
        .reduce((sum, p) => sum + (p.total_net_revenue || 0), 0);
      
      const recentSubmissions = normalizedProposals
        .filter(p => p.status !== 'draft')
        .sort((a, b) => new Date(b.submitted_at || b.created_at) - new Date(a.submitted_at || a.created_at))
        .slice(0, 5);

      // Calculate avg turnaround (days from submitted to approved/rejected)
      const processed = normalizedProposals.filter(p => 
        (p.status === 'approved' || p.status === 'rejected') && p.submitted_at && p.updated_at
      );
      let avgTurnaround = null;
      if (processed.length > 0) {
        const totalMs = processed.reduce((sum, p) => {
          return sum + (new Date(p.updated_at) - new Date(p.submitted_at));
        }, 0);
        avgTurnaround = Math.round(totalMs / processed.length / (1000 * 60 * 60 * 24) * 10) / 10;
      }
      
      setStats({
        totalProposals: normalizedProposals.length,
        pendingApprovals: pending.length,
        approvedThisMonth: approvedThisMonth.length,
        rejectedThisMonth: rejectedThisMonth.length,
        totalRevenue,
        recentSubmissions,
        draftCount: drafts.length,
        avgTurnaround
      });
      
      // Store drill-down data (normalized so columns show correct values)
      setAllProposalsData(normalizedProposals);
      setApprovedThisMonthData(approvedThisMonth);
      setApprovedRevenueData(normalizedProposals.filter(p => p.status === 'approved'));
      
      setPendingProposals(pending);

      if (isApprover && results[1]) {
        setPendingScenarios(results[1].data.proposals || []);
      }
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [isApprover]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const handleQuickApprove = async (proposal) => {
    try {
      setQuickActionId(proposal.collection_id);
      const pendingServices = proposal.services.filter(s =>
        ['pending_approval', 'pending_cfo_approval', 'pending_ceo_approval'].includes(s.status)
      );
      for (const svc of pendingServices) {
        await api.post(`/scenarios/${svc.id}/approve`, {
          action: 'approved',
          comments: 'Approved from dashboard'
        });
      }
      toast.success(`Proposal approved (${pendingServices.length} service${pendingServices.length !== 1 ? 's' : ''})`);
      fetchDashboardData();
    } catch (error) {
      console.error('Error approving:', error);
      toast.error(error.response?.data?.detail || 'Failed to approve');
    } finally {
      setQuickActionId(null);
    }
  };

  const handleQuickReject = async (proposal) => {
    if (!rejectComment.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    try {
      setQuickActionId(proposal.collection_id);
      const pendingServices = proposal.services.filter(s =>
        ['pending_approval', 'pending_cfo_approval', 'pending_ceo_approval'].includes(s.status)
      );
      for (const svc of pendingServices) {
        await api.post(`/scenarios/${svc.id}/approve`, {
          action: 'rejected',
          comments: rejectComment
        });
      }
      toast.success('Proposal rejected');
      setShowRejectModal(null);
      setRejectComment('');
      fetchDashboardData();
    } catch (error) {
      console.error('Error rejecting:', error);
      toast.error(error.response?.data?.detail || 'Failed to reject');
    } finally {
      setQuickActionId(null);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      draft: { label: 'Draft', cls: 'bg-slate-100 text-slate-600' },
      pending_approval: { label: 'Pending', cls: 'bg-amber-100 text-amber-700' },
      pending_cfo_approval: { label: 'CFO Review', cls: 'bg-orange-100 text-orange-700' },
      pending_ceo_approval: { label: 'CEO Review', cls: 'bg-red-100 text-red-700' },
      approved: { label: 'Approved', cls: 'bg-emerald-100 text-emerald-700' },
      rejected: { label: 'Rejected', cls: 'bg-red-100 text-red-700' }
    }[status] || { label: status, cls: 'bg-slate-100 text-slate-600' };
    return (
      <span data-testid={`status-badge-${status}`} className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${config.cls}`}>
        {config.label}
      </span>
    );
  };

  const fc = (amount) => new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0
  }).format(amount);

  const fd = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-3 text-sm text-slate-500">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="approver-dashboard" className="p-6 lg:p-8 space-y-8 max-w-[1400px] animate-fade-in">
      {/* Glass Header */}
      <div className="glass-card p-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 text-heading-tight">Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Welcome back, <span className="font-medium text-slate-700">{user.name || 'User'}</span></p>
        </div>
        <div className="flex items-center gap-3">
          <NotificationBell />
          <button
            data-testid="new-calculation-btn"
            onClick={() => navigate('/calculator')}
            className="flex items-center gap-2 px-6 py-2.5 bg-primary text-white rounded-full hover:bg-indigo-700 transition-all hover:-translate-y-0.5 shadow-lg shadow-primary/20 text-sm font-medium"
          >
            <Calculator className="w-4 h-4" />
            New Calculation
          </button>
          <button
            data-testid="view-proposals-btn"
            onClick={() => navigate('/proposals')}
            className="flex items-center gap-2 px-6 py-2.5 bg-white text-slate-600 border border-slate-200 rounded-full hover:bg-slate-50 transition-all text-sm font-medium"
          >
            <FileText className="w-4 h-4" />
            Proposals
          </button>
        </div>
      </div>

      {/* KPI Cards with Drill-Down - Bento Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {/* Total Proposals - Navigate directly to proposals page */}
        <div 
          className="card-modern p-6 hover:shadow-xl transition-all duration-300 cursor-pointer group relative overflow-hidden border-blue-100 hover:border-blue-200"
          onClick={() => navigate('/proposals')}
          data-testid="total-proposals-kpi"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-slate-50/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          
          <div className="relative flex items-center justify-between">
            <div className="flex-1">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Total Proposals</p>
              <p className="text-3xl font-bold text-slate-900 tracking-tight">{stats.totalProposals}</p>
              {stats.draftCount > 0 && <p className="text-xs text-slate-600 mt-2 font-medium">{stats.draftCount} draft{stats.draftCount !== 1 ? 's' : ''}</p>}
            </div>
            <div className="p-3 rounded-2xl bg-blue-50 text-blue-600 group-hover:bg-blue-100 transition-all duration-300 group-hover:scale-110 shadow-sm">
              <Briefcase className="w-6 h-6" />
            </div>
          </div>

          <div className="absolute bottom-3 right-3 flex items-center gap-1.5 text-primary font-medium text-xs opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-1 group-hover:translate-y-0">
            <span>View All →</span>
          </div>
        </div>

        <KPIDrillDown
          title="Pending Approvals"
          value={stats.pendingApprovals}
          subtitle={isApprover && pendingScenarios.length > 0 ? `${pendingScenarios.length} to review` : ''}
          data={pendingProposals}
          columns={commonColumns.pipelineHealth}
          icon={Clock}
          color={stats.pendingApprovals > 0 ? 'amber' : 'blue'}
        />

        <KPIDrillDown
          title="This Month"
          value={stats.approvedThisMonth}
          subtitle={`${stats.approvedThisMonth} approved${stats.rejectedThisMonth > 0 ? `, ${stats.rejectedThisMonth} rejected` : ''}`}
          data={approvedThisMonthData}
          columns={commonColumns.proposal}
          icon={CheckCircle}
          color="green"
        />

        <KPIDrillDown
          title="Approved Revenue"
          value={fc(stats.totalRevenue)}
          subtitle={stats.avgTurnaround !== null ? `~${stats.avgTurnaround}d avg turnaround` : ''}
          data={approvedRevenueData}
          columns={commonColumns.proposal}
          icon={DollarSign}
          color="purple"
        />
      </div>

      {/* Approval Queue (Approvers) + Recent Activity - Bento Layout */}
      <div className={`grid gap-6 ${isApprover && pendingScenarios.length > 0 ? 'grid-cols-1 xl:grid-cols-5' : 'grid-cols-1 lg:grid-cols-2'}`}>
        
        {/* Approval Queue - Main Section for Approvers */}
        {isApprover && (
          <div data-testid="approval-queue" className={`card-modern overflow-hidden ${pendingScenarios.length > 0 ? 'xl:col-span-3' : ''}`}>
            <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-amber-50/50 to-transparent">
              <h2 className="text-base font-bold text-slate-800 flex items-center gap-2.5">
                <div className="p-2 bg-amber-100 rounded-xl">
                  <AlertCircle className="w-4 h-4 text-amber-600" />
                </div>
                Approval Queue
                {pendingScenarios.length > 0 && (
                  <span className="ml-1 px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-bold">
                    {pendingScenarios.length}
                  </span>
                )}
              </h2>
              <button
                data-testid="view-all-approvals-btn"
                onClick={() => navigate('/approvals')}
                className="text-xs text-slate-500 hover:text-primary font-medium flex items-center gap-1 transition-colors"
              >
                View All <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            
            {pendingScenarios.length === 0 ? (
              <div className="py-16 text-center">
                <div className="w-16 h-16 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-emerald-500" />
                </div>
                <p className="text-sm font-medium text-slate-600">All caught up!</p>
                <p className="text-xs text-slate-500 mt-1">No pending approvals at the moment.</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-50 max-h-[420px] overflow-y-auto">
                {pendingScenarios.map(proposal => (
                  <div
                    key={proposal.collection_id}
                    data-testid={`approval-item-${proposal.collection_id}`}
                    className="px-5 py-3.5 hover:bg-slate-50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/proposal/${proposal.collection_id}`)}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-medium text-slate-900 text-sm truncate">{proposal.collection_name || 'Proposal'}</p>
                          {getStatusBadge(proposal.status)}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {proposal.submitter_name || proposal.creator_name || 'Unknown'}
                          </span>
                          <span>{proposal.service_count} service{proposal.service_count !== 1 ? 's' : ''}</span>
                          <span className="font-medium text-slate-700">${((proposal.aggregated_totals?.gross_revenue || 0) / 1000).toFixed(1)}K</span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {fd(proposal.submitted_at)}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          data-testid={`review-btn-${proposal.collection_id}`}
                          onClick={(e) => { e.stopPropagation(); navigate(`/proposal/${proposal.collection_id}`); }}
                          className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors"
                          title="View proposal"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {proposal.can_current_user_approve !== false ? (
                          <>
                            <button
                              data-testid={`quick-approve-btn-${proposal.collection_id}`}
                              onClick={(e) => { e.stopPropagation(); handleQuickApprove(proposal); }}
                              disabled={quickActionId === proposal.collection_id}
                              className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-md transition-colors disabled:opacity-50"
                              title="Quick approve all"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                            <button
                              data-testid={`quick-reject-btn-${proposal.collection_id}`}
                              onClick={(e) => { e.stopPropagation(); setShowRejectModal(proposal); }}
                              disabled={quickActionId === proposal.collection_id}
                              className="p-1.5 text-red-500 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50"
                              title="Reject"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          </>
                        ) : (
                          <span className="text-xs text-slate-400 px-1.5">Escalated</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Recent Activity */}
        <div data-testid="recent-activity" className={`card-modern overflow-hidden ${isApprover && pendingScenarios.length > 0 ? 'xl:col-span-2' : ''}`}>
          <div className="px-6 py-5 border-b border-slate-100 bg-gradient-to-r from-sky-50/50 to-transparent">
            <h2 className="text-base font-bold text-slate-800 flex items-center gap-2.5">
              <div className="p-2 bg-sky-100 rounded-xl">
                <Activity className="w-4 h-4 text-sky-600" />
              </div>
              Recent Activity
            </h2>
          </div>
          
          {stats.recentSubmissions.length === 0 ? (
            <div className="py-16 text-center">
              <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-slate-300" />
              </div>
              <p className="text-sm font-medium text-slate-600">No recent activity</p>
              <p className="text-xs text-slate-500 mt-1">Your submissions will appear here</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-50 max-h-[420px] overflow-y-auto">
              {stats.recentSubmissions.map((proposal, idx) => (
                <div
                  key={proposal.id}
                  data-testid={`recent-item-${proposal.id}`}
                  onClick={() => navigate(`/proposal/${proposal.id}`)}
                  className="px-6 py-4 hover:bg-gradient-to-r hover:from-slate-50 hover:to-transparent transition-all cursor-pointer group"
                  style={{ animationDelay: `${idx * 50}ms` }}
                >
                  <div className="flex items-start justify-between mb-2">
                    <p className="font-semibold text-slate-900 text-sm truncate group-hover:text-primary transition-colors">{proposal.name}</p>
                    {getStatusBadge(proposal.status)}
                  </div>
                  <div className="flex items-center gap-4 text-xs text-slate-600">
                    <span className="flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5 text-slate-400" />
                      {fd(proposal.submitted_at || proposal.updated_at)}
                    </span>
                    {proposal.total_net_revenue > 0 && (
                      <span className="font-semibold text-primary px-2 py-0.5 bg-primary/5 rounded-full">
                        {fc(proposal.total_net_revenue)}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Action Items for non-approvers */}
        {!isApprover && (
          <div data-testid="pending-items" className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100">
              <h2 className="font-semibold text-slate-900 flex items-center gap-2">
                <Clock className="w-4 h-4 text-amber-500" />
                Your Pending Items
              </h2>
            </div>
            {pendingProposals.length === 0 ? (
              <div className="py-12 text-center">
                <CheckCircle className="w-10 h-10 text-emerald-400 mx-auto mb-2" />
                <p className="text-sm text-slate-500">No pending items</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {pendingProposals.slice(0, 5).map(proposal => (
                  <div
                    key={proposal.id}
                    onClick={() => navigate(`/proposal/${proposal.id}`)}
                    className="px-5 py-3.5 hover:bg-slate-50 transition-colors cursor-pointer"
                  >
                    <div className="flex items-start justify-between mb-1">
                      <p className="font-medium text-slate-900 text-sm">{proposal.name}</p>
                      {getStatusBadge(proposal.status)}
                    </div>
                    <p className="text-xs text-slate-500">{fd(proposal.submitted_at || proposal.created_at)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Performance Insights - NEW */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {/* Win Rate Card */}
        <div className="card-modern p-6 bg-gradient-to-br from-emerald-50/50 via-white to-white border-emerald-100">
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wider mb-1">Win Rate</p>
              <p className="text-3xl font-bold text-emerald-600">
                {stats.approvedThisMonth + stats.rejectedThisMonth > 0 
                  ? Math.round((stats.approvedThisMonth / (stats.approvedThisMonth + stats.rejectedThisMonth)) * 100)
                  : 0}%
              </p>
            </div>
            <div className="p-3 bg-emerald-100 rounded-2xl">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            </div>
          </div>
          <p className="text-xs text-slate-600">
            <span className="font-semibold text-emerald-700">{stats.approvedThisMonth}</span> approved, {stats.rejectedThisMonth} rejected this month
          </p>
        </div>

        {/* Avg Turnaround Card */}
        <div className="card-modern p-6 bg-gradient-to-br from-sky-50/50 via-white to-white border-sky-100">
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="text-xs font-semibold text-sky-700 uppercase tracking-wider mb-1">Avg Turnaround</p>
              <p className="text-3xl font-bold text-sky-600">
                {stats.avgTurnaround !== null ? `${stats.avgTurnaround}d` : 'N/A'}
              </p>
            </div>
            <div className="p-3 bg-sky-100 rounded-2xl">
              <Clock className="w-5 h-5 text-sky-600" />
            </div>
          </div>
          <p className="text-xs text-slate-600">
            Time from submission to approval decision
          </p>
        </div>

        {/* Active Proposals Card */}
        <div className="card-modern p-6 bg-gradient-to-br from-amber-50/50 via-white to-white border-amber-100">
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="text-xs font-semibold text-amber-700 uppercase tracking-wider mb-1">In Progress</p>
              <p className="text-3xl font-bold text-amber-600">{stats.draftCount}</p>
            </div>
            <div className="p-3 bg-amber-100 rounded-2xl">
              <Briefcase className="w-5 h-5 text-amber-600" />
            </div>
          </div>
          <p className="text-xs text-slate-600">
            Draft proposals awaiting completion
          </p>
        </div>
      </div>

      {/* Quick Actions - Enhanced */}
      <div className="card-modern p-6">
        <h2 className="text-base font-bold text-slate-800 mb-5 flex items-center gap-2">
          <div className="w-1 h-6 bg-gradient-to-b from-primary to-accent rounded-full"></div>
          Quick Actions
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button
            data-testid="qa-calculator"
            onClick={() => navigate('/calculator')}
            className="group flex flex-col items-center gap-3 p-5 bg-gradient-to-br from-sky-50 to-transparent rounded-2xl hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border border-sky-100"
          >
            <div className="p-3 bg-sky-100 rounded-2xl group-hover:scale-110 group-hover:bg-sky-200 transition-all duration-300">
              <Calculator className="w-6 h-6 text-sky-600" />
            </div>
            <span className="text-sm font-semibold text-slate-700 group-hover:text-sky-700 transition-colors">Calculator</span>
          </button>
          <button
            data-testid="qa-proposals"
            onClick={() => navigate('/proposals')}
            className="group flex flex-col items-center gap-3 p-5 bg-gradient-to-br from-blue-50 to-transparent rounded-2xl hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border border-blue-100"
          >
            <div className="p-3 bg-blue-100 rounded-2xl group-hover:scale-110 group-hover:bg-blue-200 transition-all duration-300">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
            <span className="text-sm font-semibold text-slate-700 group-hover:text-blue-700 transition-colors">Proposals</span>
          </button>
          {isApprover && (
            <button
              data-testid="qa-analytics"
              onClick={() => navigate('/analytics')}
              className="group flex flex-col items-center gap-3 p-5 bg-gradient-to-br from-emerald-50 to-transparent rounded-2xl hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border border-emerald-100"
            >
              <div className="p-3 bg-emerald-100 rounded-2xl group-hover:scale-110 group-hover:bg-emerald-200 transition-all duration-300">
                <BarChart3 className="w-6 h-6 text-emerald-600" />
              </div>
              <span className="text-sm font-semibold text-slate-700 group-hover:text-emerald-700 transition-colors">Analytics</span>
            </button>
          )}
          <button
            data-testid="qa-history"
            onClick={() => navigate('/calculation-history')}
            className="group flex flex-col items-center gap-3 p-5 bg-gradient-to-br from-violet-50 to-transparent rounded-2xl hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border border-violet-100"
          >
            <div className="p-3 bg-violet-100 rounded-2xl group-hover:scale-110 group-hover:bg-violet-200 transition-all duration-300">
              <TrendingUp className="w-6 h-6 text-violet-600" />
            </div>
            <span className="text-sm font-semibold text-slate-700 group-hover:text-violet-700 transition-colors">History</span>
          </button>
        </div>
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div data-testid="reject-modal" className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-1">Reject Proposal</h3>
            <p className="text-sm text-slate-500 mb-4">
              {showRejectModal.collection_name || 'Proposal'} — {showRejectModal.service_count || 1} service{(showRejectModal.service_count || 1) !== 1 ? 's' : ''}
            </p>
            <textarea
              data-testid="reject-comment-input"
              value={rejectComment}
              onChange={(e) => setRejectComment(e.target.value)}
              className="w-full border border-slate-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none resize-none"
              rows={3}
              placeholder="Enter rejection reason..."
              autoFocus
            />
            <div className="flex justify-end gap-2 mt-4">
              <button
                data-testid="reject-cancel-btn"
                onClick={() => { setShowRejectModal(null); setRejectComment(''); }}
                className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                data-testid="reject-confirm-btn"
                onClick={() => handleQuickReject(showRejectModal)}
                disabled={quickActionId === showRejectModal.collection_id}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
